/**
 * Collapsible — Base UI Collapsible primitive.
 *
 * Design system mapping:
 *   • Trigger: full-width row, bg-webex-gray-50 dark:bg-webex-gray-900/50,
 *     border webex-gray-300 dark:border-webex-gray-700, rounded-md px-3 py-2
 *   • Chevron: 14px, rotates 180deg when open
 *   • Panel: animates height 200ms (via CSS var --collapsible-panel-height)
 *
 * Composition:
 *   <Collapsible.Root defaultOpen>
 *     <Collapsible.Trigger>Section title</Collapsible.Trigger>
 *     <Collapsible.Panel>...</Collapsible.Panel>
 *   </Collapsible.Root>
 */
import * as React from "react"
import { Collapsible as BaseCollapsible } from "@base-ui-components/react/collapsible"
import { cn } from "@lib/cn"

const Root = BaseCollapsible.Root

const Trigger = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<typeof BaseCollapsible.Trigger>
>(({ className, children, ...props }, ref) => (
  <BaseCollapsible.Trigger
    ref={ref as unknown as React.Ref<HTMLButtonElement>}
    className={cn(
      "flex w-full items-center justify-between gap-2 rounded-md border",
      "bg-webex-gray-50 dark:bg-webex-gray-900/50",
      "border-webex-gray-300 dark:border-webex-gray-700",
      "px-3 py-2 text-sm font-medium text-webex-gray-1000 dark:text-white",
      "cursor-pointer select-none",
      "transition-colors duration-150 motion-reduce:transition-none",
      "hover:bg-webex-gray-100 dark:hover:bg-webex-gray-850",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-webex-blue-600 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-webex-gray-950",
      "[&[data-panel-open]_svg]:rotate-180",
      className,
    )}
    {...props}
  >
    <span className="flex-1 text-left">{children}</span>
    <svg
      viewBox="0 0 14 14"
      className="size-3.5 shrink-0 transition-transform duration-150 motion-reduce:transition-none"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="3,5.5 7,9.5 11,5.5" />
    </svg>
  </BaseCollapsible.Trigger>
))
Trigger.displayName = "Collapsible.Trigger"

const Panel = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof BaseCollapsible.Panel>
>(({ className, children, ...props }, ref) => (
  <BaseCollapsible.Panel
    ref={ref}
    className={cn(
      "overflow-hidden",
      "h-[var(--collapsible-panel-height)]",
      "data-[starting-style]:h-0 data-[ending-style]:h-0",
      "transition-[height] duration-200 motion-reduce:transition-none",
      className,
    )}
    {...props}
  >
    <div className="px-3 py-3">{children}</div>
  </BaseCollapsible.Panel>
))
Panel.displayName = "Collapsible.Panel"

export const Collapsible = { Root, Trigger, Panel }
